if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        try {
          this.productVariants = JSON.parse(this.dataset.productVariants || '[]');
        } catch (_error) {
          this.productVariants = [];
        }

        this.dynamicButtonCopy = this.dynamicButtonCopy.bind(this);
        this.dynamicButtonCopy();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.variant) {
            this.dynamicButtonCopy(event.data.variant);
          }
        });

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      isVipVariant(variant) {
        const title = variant?.option1 || variant?.title || '';
        return title.toLowerCase().includes('vip');
      }

      resolveVariant(passedVariant) {
        if (passedVariant) return passedVariant;
        if (window.__dynamicButtonVariant) return window.__dynamicButtonVariant;

        const selectedVariantScript = this.closest('product-info')?.querySelector('[data-selected-variant]');
        if (selectedVariantScript?.textContent) {
          try {
            return JSON.parse(selectedVariantScript.textContent);
          } catch (_error) {
            // ignore parse error, fall back below
          }
        }

        const hiddenVariantInput = this.querySelector('.product-variant-id');
        const fallbackVariantId = hiddenVariantInput?.value;
        if (!fallbackVariantId) return null;

        const fromDataset = this.productVariants?.find((item) => String(item.id) === String(fallbackVariantId));
        if (fromDataset) return fromDataset;

        const modalVariantsRaw = document.querySelector('product-form-modal')?.dataset.productVariants;
        if (modalVariantsRaw) {
          try {
            const productVariants = JSON.parse(modalVariantsRaw);
            return productVariants.find((item) => String(item.id) === String(fallbackVariantId)) || null;
          } catch (_error) {
            return null;
          }
        }

        return null;
      }

      getVipStatus() {
        const timerElement = document.querySelector('.vip-timer-container');
        const timerInstance = timerElement?._vipTimerInstance;
        if (timerInstance?.calculateWindowStatus) {
          try {
            return timerInstance.calculateWindowStatus();
          } catch (_error) {
            // ignore and try fallback
          }
        }

        const vipRadio = document.querySelector('input[value="VIP"]');
        if (vipRadio?.classList.contains('vip-unavailable')) {
          return { status: 'closed_opens_later' };
        }
        if (vipRadio?.classList.contains('vip-available')) {
          return { status: 'open' };
        }

        return null;
      }

      formatPrice(value) {
        return Math.round(Number(value || 0) / 100);
      }

      applyButtonState(text, disable) {
        if (!this.submitButton || !this.submitButtonText) return;
        this.submitButtonText.textContent = text;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      dynamicButtonCopy(passedVariant) {
        if (!this.submitButtonText) return;

        const currentVariant = this.resolveVariant(passedVariant);
        if (!currentVariant) return;

        let disableButton = false;
        let buttonText = '';

        const variantName = currentVariant.option1 || currentVariant.title || 'Variant';

        if (!currentVariant.available) {
          buttonText = window.variantStrings?.soldOut || 'Sold out';
          disableButton = true;
        } else if (this.isVipVariant(currentVariant)) {
          const vipStatus = this.getVipStatus();
          const status = vipStatus?.status || (document.querySelector('input[value="VIP"]')?.classList.contains('vip-unavailable') ? 'closed_opens_later' : 'open');

          switch (status) {
            case 'sold_out':
              buttonText = 'VIP Sold Out';
              disableButton = false;
              break;
            case 'closed_opens_later':
            case 'closed_opens_tomorrow':
            case 'weekend_blocked':
            case 'closed':
              buttonText = 'VIP Closed';
              disableButton = false;
              break;
            default:
              buttonText = 'Book VIP Now';
              disableButton = false;
          }
        } else {
          buttonText = `Book ${variantName} Now`;
        }

        this.applyButtonState(buttonText, disableButton);
        window.__dynamicButtonVariant = currentVariant;
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }
    }
  );
}
